'use client'

import React, { useState } from 'react'
import { OpenPosition } from '../../_data/careers'
import { Button } from '@/components/ui/Button'

interface OpenPositionsProps {
    positions?: (OpenPosition & { _id?: string })[]
    title?: string
    subtitle?: string
    searchPlaceholder?: string
    noResults?: string
    applyButton?: string
}

export const OpenPositions = ({ positions, title, subtitle, searchPlaceholder, noResults, applyButton }: OpenPositionsProps = {}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const allPositions = positions ?? []
    const resolvedTitle = title
    const resolvedSubtitle = subtitle
    const resolvedSearchPlaceholder = searchPlaceholder
    const resolvedNoResults = noResults
    const resolvedApplyButton = applyButton

    const filteredPositions = allPositions.filter((pos: OpenPosition) =>
        pos.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pos.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pos.location.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return (
        <section id="open-positions" className="bg-[#f8f7fa] pt-20 lg:pt-40" data-theme="light">
            <div className="container mx-auto px-6 lg:px-0">
                <div className="mb-12 lg:mb-16">
                    <h2 className="text-[48px] lg:text-[72px] font-bricolage leading-tight font-bold text-[#0B0623] mb-4">
                        {resolvedTitle}
                    </h2>
                    <p className="font-sans max-w-[700px] leading-tight text-[16px] lg:text-[18px] text-[#302B44] font-medium mb-10 lg:mb-12">
                        {resolvedSubtitle}
                    </p>

                    <div className="relative flex items-center gap-2 max-w-[560px] py-[14px] px-[24px] h-[52px] rounded-[40px] border-2 border-transparent bg-white focus-within:border-[#7C38F6]">
                        <span className='min-w-6 w-6'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M19.6 21L13.3 14.7C12.8 15.1 12.225 15.4167 11.575 15.65C10.925 15.8833 10.2333 16 9.5 16C7.68333 16 6.14583 15.3708 4.8875 14.1125C3.62917 12.8542 3 11.3167 3 9.5C3 7.68333 3.62917 6.14583 4.8875 4.8875C6.14583 3.62917 7.68333 3 9.5 3C11.3167 3 12.8542 3.62917 14.1125 4.8875C15.3708 6.14583 16 7.68333 16 9.5C16 10.2333 15.8833 10.925 15.65 11.575C15.4167 12.225 15.1 12.8 14.7 13.3L21 19.6L19.6 21ZM9.5 14C10.75 14 11.8125 13.5625 12.6875 12.6875C13.5625 11.8125 14 10.75 14 9.5C14 8.25 13.5625 7.1875 12.6875 6.3125C11.8125 5.4375 10.75 5 9.5 5C8.25 5 7.1875 5.4375 6.3125 6.3125C5.4375 7.1875 5 8.25 5 9.5C5 10.75 5.4375 11.8125 6.3125 12.6875C7.1875 13.5625 8.25 14 9.5 14Z" fill="#918F9C" />
                            </svg>
                        </span>

                        <input
                            type="text"
                            placeholder={resolvedSearchPlaceholder}
                            className="w-full font-sans focus:outline-none  text-[16px] font-medium text-[#0B0623] placeholder:text-[#666]"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-4">
                    {filteredPositions.length > 0 ? (
                        filteredPositions.map((pos: OpenPosition & { _id?: string }) => (
                            <div
                                key={pos.id ?? pos._id}
                                className="group flex flex-col lg:flex-row items-start lg:items-center justify-between p-6 lg:p-8 lg:pr-[40px] rounded-[31px] bg-white gap-6 lg:gap-0"
                            >
                                <div className="flex flex-col gap-4">
                                    <h3 className="font-bricolage tracking-[-0.48px] text-[20px] lg:text-[24px] font-bold text-[#0B0623]">
                                        {pos.title}
                                    </h3>
                                    <div className="flex flex-wrap gap-x-4 gap-y-3 font-sans text-[14px] lg:text-[16px] text-[#302B44] font-medium">
                                        <div className="flex items-center gap-2 w-full md:w-auto">
                                            <span className='w-[24px] h-[21px] flex items-center justify-center shrink-0'>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <path d="M13.4125 11.4125C13.8042 11.0208 14 10.55 14 10C14 9.45 13.8042 8.97917 13.4125 8.5875C13.0208 8.19583 12.55 8 12 8C11.45 8 10.9792 8.19583 10.5875 8.5875C10.1958 8.97917 10 9.45 10 10C10 10.55 10.1958 11.0208 10.5875 11.4125C10.9792 11.8042 11.45 12 12 12C12.55 12 13.0208 11.8042 13.4125 11.4125ZM12 19.35C14.0333 17.4833 15.5417 15.7875 16.525 14.2625C17.5083 12.7375 18 11.3833 18 10.2C18 8.38333 17.4208 6.89583 16.2625 5.7375C15.1042 4.57917 13.6833 4 12 4C10.3167 4 8.89583 4.57917 7.7375 5.7375C6.57917 6.89583 6 8.38333 6 10.2C6 11.3833 6.49167 12.7375 7.475 14.2625C8.45833 15.7875 9.96667 17.4833 12 19.35ZM12 22C9.31667 19.7167 7.3125 17.5958 5.9875 15.6375C4.6625 13.6792 4 11.8667 4 10.2C4 7.7 4.80417 5.70833 6.4125 4.225C8.02083 2.74167 9.88333 2 12 2C14.1167 2 15.9792 2.74167 17.5875 4.225C19.1958 5.70833 20 7.7 20 10.2C20 11.8667 19.3375 13.6792 18.0125 15.6375C16.6875 17.5958 14.6833 19.7167 12 22Z" fill="#918F9C" />
                                                </svg>
                                            </span>
                                            {pos.location}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className='w-[24px] h-[21px] flex items-center justify-center shrink-0'>
                                                <svg width="5" height="5" viewBox="0 0 5 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1.98269 4.04835C1.62002 4.04835 1.28402 3.95768 0.974688 3.77635C0.676021 3.59502 0.436021 3.35502 0.254688 3.05635C0.0840209 2.74702 -0.00131243 2.40568 -0.00131243 2.03235C-0.00131243 1.64835 0.0840209 1.30702 0.254688 1.00835C0.436021 0.699018 0.676021 0.453684 0.974688 0.272351C1.28402 0.091018 1.62002 0.000351429 1.98269 0.000351429C2.36669 0.000351429 2.70802 0.091018 3.00669 0.272351C3.31602 0.453684 3.56135 0.699018 3.74269 1.00835C3.92402 1.30702 4.01469 1.64835 4.01469 2.03235C4.01469 2.40568 3.92402 2.74702 3.74269 3.05635C3.56135 3.35502 3.31602 3.59502 3.00669 3.77635C2.70802 3.95768 2.36669 4.04835 1.98269 4.04835Z" fill="#302B44" />
                                                </svg>
                                            </span>
                                            {pos.category}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className='w-[24px] h-[21px] flex items-center justify-center shrink-0'>
                                                <svg width="5" height="5" viewBox="0 0 5 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1.98269 4.04835C1.62002 4.04835 1.28402 3.95768 0.974688 3.77635C0.676021 3.59502 0.436021 3.35502 0.254688 3.05635C0.0840209 2.74702 -0.00131243 2.40568 -0.00131243 2.03235C-0.00131243 1.64835 0.0840209 1.30702 0.254688 1.00835C0.436021 0.699018 0.676021 0.453684 0.974688 0.272351C1.28402 0.091018 1.62002 0.000351429 1.98269 0.000351429C2.36669 0.000351429 2.70802 0.091018 3.00669 0.272351C3.31602 0.453684 3.56135 0.699018 3.74269 1.00835C3.92402 1.30702 4.01469 1.64835 4.01469 2.03235C4.01469 2.40568 3.92402 2.74702 3.74269 3.05635C3.56135 3.35502 3.31602 3.59502 3.00669 3.77635C2.70802 3.95768 2.36669 4.04835 1.98269 4.04835Z" fill="#302B44" />
                                                </svg>
                                            </span>
                                            {pos.type}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="primary"
                                    href={(pos as any).applicationLink ?? pos.link}
                                    className="h-[40px] px-6 text-[14px] font-medium font-sans w-full lg:w-auto"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {resolvedApplyButton}
                                </Button>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-20 bg-white rounded-[24px]">
                            <p className="font-sans leading-tight text-[16px] lg:text-[18px] text-[#302B44] font-medium">
                                {resolvedNoResults}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
